import { useSharedContext } from '../../context/SharedContext';
import React, { useEffect, useRef, useState } from 'react';
import { DistributorDto } from '../../api/dto/DistributorDto';
import { AuthenticatedUser } from '../../security/auth/AuthenticatedUser';
import { ShopifySupportApi } from '../../api/ShopifySupportApi';
import { InventoryApi } from '../../api/InventoryApi';
import { DistributorItemDto } from '../../api/dto/DistributorItemDto';
import { Alerter } from '../../layout/Alerter';
import { ProductTemplate } from '../../utils/shopify/ProductTemplate';
import { SkuDto } from '../../api/dto/SkuDto';

export default function SkusFromDistributor() {

    const sharedContextData = useSharedContext();

    const distributors = useRef<DistributorDto[] | null>(null);
    const selectedDistributor = useRef<DistributorDto | null>(null);
    const [itemIdentifier, setItemIdentifier] = useState<string | null>(null);
    const [distributorItems, setDistributorItems] = useState<DistributorItemDto[] | null>(() => null);

    const skusToAdd = useRef<DistributorItemDto[]>([]);

    const [valueChanged, setValueChanged] = useState(0);

    const loadDistributors = async (user: AuthenticatedUser) => {
        const lookup = await ShopifySupportApi.getDistributors(user);
        if (lookup) {
            distributors.current = lookup.filter(distributor => !distributor.name.toUpperCase().startsWith("MAKE MY CAP"));

            if (distributors.current != null && distributors.current.length > 0) {
                selectedDistributor.current = distributors.current[0];
            }
            setValueChanged(valueChanged + 1);
        }
    }

    function selectDistributor(code: string) {
        const selection = distributors.current!.find((distributor) => distributor.code == code);
        if (selection) {
            selectedDistributor.current = selection;
            setValueChanged(valueChanged + 1);
        }
    }

    function loadItems() {
        if (selectedDistributor.current == null) {
            Alerter.showWarning("You must specify a distributor to search", Alerter.DEFAULT_TIMEOUT);
            return;
        } else if (itemIdentifier == null || itemIdentifier.length == 0) {
            Alerter.showWarning("You must specify an item identifier/style to search", Alerter.DEFAULT_TIMEOUT);
            return;
        }
        loadDistributorItems(selectedDistributor.current.code, itemIdentifier).catch((error) => console.log(error));
        skusToAdd.current = [];
    }

    function toggleSku(item: DistributorItemDto) {
        const match = skusToAdd.current.find(sku => sku.getId() == item.getId());
        if (match) {
            skusToAdd.current = skusToAdd.current.filter(sku => sku.getId() != item.getId());
        } else {
            skusToAdd.current.push(item);
        }
        setValueChanged(valueChanged + 1);
    }

    function addMappings() {

        const skuDtos: SkuDto[] = [];
        skusToAdd.current.forEach(sku => {
            const skuDto = new SkuDto(sku.suggestedSku!, sku.distributorCode!, sku.distributorSku, sku.brand,
                                    sku.styleCode, sku.partId, sku.color, sku.colorCode, sku.sizeCode,null);
            skuDtos.push(skuDto);
        });

        if (skuDtos.length == 0) {
            return;
        }
        if (!confirm(`Are you sure you want to add the ${skuDtos.length} selected distributor items to MMC inventory database?`)) {
            return;
        }

        const csv = new ProductTemplate(selectedDistributor.current!, itemIdentifier!);
        skusToAdd.current.forEach(sku => csv.addItem(sku));

        addNewSkus(skuDtos)
            .then(() => {
                downloadCSV(csv);
                loadItems();
                Alerter.showSuccess("Skus created and csv downloaded", Alerter.DEFAULT_TIMEOUT);
            })
            .catch((error) => {
                console.log(error)
                Alerter.showError("Error encountered while creating skus", Alerter.DEFAULT_TIMEOUT);
            });
    }

    const addNewSkus = async (skuDtos: SkuDto[]): Promise<boolean> => {
        const authenticatedUser = sharedContextData.getAuthenticatedUser();
        if (authenticatedUser) {
            return await ShopifySupportApi.addSkus(skuDtos, authenticatedUser);
        }
        console.log("Unable to find authenticated user to save new skus!");
        return false;
    }

    function downloadCSV(csv: ProductTemplate) {

        const blob = new Blob([csv.getContents()], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = csv.fileName;
        document.body.appendChild(link); // Append to body
        link.click(); // Simulate click

        // Clean up by revoking the Blob URL and removing the link
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
    }

    const loadDistributorItems = async (distributorCode: string, inventoryItem: string) => {
        const authenticatedUser = sharedContextData.getAuthenticatedUser();
        if (authenticatedUser) {
            const lookup = await InventoryApi.fetchMatchingDistributorItems(distributorCode, inventoryItem, authenticatedUser);
            if (lookup) {
                setDistributorItems(lookup)
            } else {
                setDistributorItems([]);
                Alerter.showInfo("There were no items that matched the distributor and item/style # found.", Alerter.DEFAULT_TIMEOUT);
            }
        }
    }


    useEffect(() => {
        // for forcing redraw
    }, [valueChanged]);

    useEffect(() => {
        const user = sharedContextData.getAuthenticatedUser();
        if (user != null) {
            loadDistributors(user)
                .catch(() => {
                    console.log("Error loading distributors")
                });
        }
    }, []);

    return (
        <div>
            <h2><span className="ca-blue">Create SKUs from Distributor Items</span></h2>
            {
                distributors.current == null &&
                <div className="mmc-spinner-box spinner-border text-primary"><span className="sr-only"></span></div>
            }
            {
                distributors.current != null &&
                <div>
                    <div>
                        <div className="skumap-selections">
                            <div className="skumap-prompt-box">
                                <label htmlFor="SelectDistributor" className="col-form-label">Select distributor:</label>
                                <select id="SelectDistributor" className="form-control"
                                        value={selectedDistributor.current == null ? '' : selectedDistributor.current.code}
                                        onChange={(e) => selectDistributor(e.target.value)}>
                                    {
                                        (distributors.current!).map((distributor: DistributorDto) => {
                                            return (
                                                <option key={distributor.code} value={distributor.code}>
                                                    {distributor.name}
                                                </option>
                                            );
                                        })
                                    }
                                </select>
                            </div>
                            <div className="skumap-prompt-box">
                                <label htmlFor="ItemOrStyle" className="col-form-label">Item/Style #:</label>
                                <input id="ItemOrStyle" className="form-control" type="text" maxLength={20} required
                                       onChange={e => setItemIdentifier(e.target.value)}/>
                            </div>
                            <div className="skumap-action-box">
                                <button id="LoadItems" type="button"
                                        className="form-control skumap-action-button btn btn-primary"
                                        onClick={() => loadItems()}>Load items
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="mmc-tabular-data-container">
                        <table className="table table-striped table-hover">
                            <thead className="table-dark">
                            <tr>
                                <th>Add?</th>
                                <th>MMC Sku</th>
                                <th>Dist Sku</th>
                                <th>Brand</th>
                                <th>Style Code</th>
                                <th>Part Id</th>
                                <th>Color</th>
                                <th>Size</th>
                            </tr>
                            </thead>
                            <tbody>
                            {
                                distributorItems != null && (distributorItems).map(function (item) {
                                    return (
                                        <tr key={item.getId()}>
                                            <td>{item.alreadyMapped ? "Mapped" : <input type="checkbox" onClick={e => toggleSku(item)}></input>}</td>
                                            <td>{item.suggestedSku}</td>
                                            <td>{item.distributorSku}</td>
                                            <td>{item.brand}</td>
                                            <td>{item.styleCode}</td>
                                            <td>{item.partId}</td>
                                            <td>{item.color}</td>
                                            <td>{item.sizeCode}</td>
                                        </tr>
                                    );
                                })
                            }
                            </tbody>
                        </table>
                        {skusToAdd.current.length > 0 &&
                            <div>
                                <button id="AddNewMappings" type="button"
                                         className="form-control skumap-action-button btn btn-primary"
                                         onClick={() => addMappings()}>Add new mappings</button>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    );
}
